import React, { useEffect, useMemo, useState } from "react";
import { getAnnonces } from "./../../../services/annoncesServices";
import { useUser } from "./../../Home/UserContext";
import { useNotification } from "../../../components/Notification/NotificationManager";
import useScrollToTop from "./../../../hooks/useScollToTop";
import Button from "./../../../components/Button/Button";
import CreateAnnonceModal from "./CreateAnnonceModal";
import AnnonceCard from "./AnnonceCard";
import { useNavigate } from "react-router-dom";
import { FaSearch } from "react-icons/fa";
import { MdOutlineUnfoldMore } from "react-icons/md";

const CATEGORIES = [
  "Recherche Enseignant",
  "Enseignant cherche club",
  "Recherche Matériel",
  "Don Matériel",
  "Autres",
];

const AnnoncesPage = () => {
  useScrollToTop();
  const navigate = useNavigate();
  const { user } = useUser();
  const roleId = user?.role_id ?? null;     // ex: 1,3,6,7,8…
  const currentUserId = user?.userId ?? null;
  const canModerate = [1, 6, 7].includes(Number(roleId));       // admin/ligue
  const canCreate = [1, 3, 6, 7, 8].includes(Number(roleId));   // + dirigeants/enseignants

  const { addNotification } = useNotification();

  const [allAnnonces, setAllAnnonces] = useState([]);
  const [loading, setLoading] = useState(true);

  // UI / filtres
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [showAll, setShowAll] = useState(false); // visible seulement si canModerate
  const [searchTitle, setSearchTitle] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [sortKey, setSortKey] = useState("date_desc"); // date_desc | date_asc | title_az | title_za

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const response = await getAnnonces();
        setAllAnnonces(Array.isArray(response) ? response : []);
      } catch (error) {
        console.error("Failed to fetch annonces:", error);
        addNotification("Erreur lors du chargement des annonces.", "error");
      } finally {
        setLoading(false);
      }
    })();
  }, [addNotification]);

  const filteredSorted = useMemo(() => {
    const norm = (s = "") =>
      s.toString().normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

    let rows = [...allAnnonces];

    // Public : on ne montre que publiées ; Modération : contrôle via showAll
    if (!canModerate || !showAll) rows = rows.filter((a) => a.is_active);

    if (searchTitle.trim()) {
      const q = norm(searchTitle);
      rows = rows.filter((a) => norm(a.title).includes(q));
    }

    if (selectedCategory) {
      rows = rows.filter((a) => a.category === selectedCategory);
    }

    rows.sort((a, b) => {
      if (sortKey === "date_desc") {
        return new Date(b.date_annonce) - new Date(a.date_annonce);
      }
      if (sortKey === "date_asc") {
        return new Date(a.date_annonce) - new Date(b.date_annonce);
      }
      if (sortKey === "title_az") {
        return a.title.localeCompare(b.title);
      }
      if (sortKey === "title_za") {
        return b.title.localeCompare(a.title);
      }
      return 0;
    });

    return rows;
  }, [allAnnonces, canModerate, showAll, searchTitle, selectedCategory, sortKey]);

  const openCreate = () => setIsCreateModalOpen(true);
  const reload = async () => {
    try {
      const response = await getAnnonces();
      setAllAnnonces(Array.isArray(response) ? response : []);
    } catch {
      // silencieux
    }
  };
  const closeCreate = async () => {
    setIsCreateModalOpen(false);
    await reload();
  };

  const resetFilters = () => {
    setSearchTitle("");
    setSelectedCategory("");
    setSortKey("date_desc");
    setShowAll(false);
  };

  return (
    <div className="w-full pt-[100px] font-satoshi">
      {/* Hero */}
      <div className="relative">
        <div
          className="w-full h-72 bg-cover bg-center"
          style={{ backgroundImage: `url('/img/illustration_petite_annonce_qb825q.webp')` }}
          role="img"
          aria-label="Illustration petites annonces"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/30 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 text-white p-4 max-w-[1280px] mx-auto">
          <p className="text-sm">
            <span onClick={() => navigate("/")} className="cursor-pointer hover:underline">
              Accueil
            </span>{" "}
            &gt; Ligue &gt; Petites Annonces
          </p>
          <div className="flex flex-wrap gap-2 items-end justify-between">
            <h1 className="text-2xl md:text-4xl font-bold">Petites Annonces</h1>
            {canCreate && (
              <Button
                text="Ajouter une annonce"
                onClick={openCreate}
                className="!rounded-xl"
              />
            )}
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="max-w-[1280px] mx-auto px-4 mt-6">
        <div className="bg-white/90 dark:bg-slate-800/80 ring-1 ring-slate-200/70 dark:ring-slate-700/70 rounded-2xl p-4 flex flex-col gap-3">
          <div className="flex flex-col lg:flex-row gap-3 items-stretch lg:items-center">
            <div className="flex-1 relative">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 opacity-60" />
              <input
                value={searchTitle}
                onChange={(e) => setSearchTitle(e.target.value)}
                placeholder="Rechercher par titre…"
                className="w-full pl-9 pr-3 py-2 rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70"
              />
            </div>

            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-3 py-2 rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70"
            >
              <option value="">Toutes les catégories</option>
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>

            <select
              value={sortKey}
              onChange={(e) => setSortKey(e.target.value)}
              className="px-3 py-2 rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70"
            >
              <option value="date_desc">Plus récentes</option>
              <option value="date_asc">Plus anciennes</option>
              <option value="title_az">Titre A→Z</option>
              <option value="title_za">Titre Z→A</option>
            </select>

            {canModerate && (
              <Button
                text={
                  showAll
                    ? "Afficher seulement publiées"
                    : "Afficher tout (y compris non publiées)"
                }
                onClick={() => setShowAll((s) => !s)}
                icon={MdOutlineUnfoldMore}
                variant="subtle"
                className="!rounded-xl"
              />
            )}

            <Button
              text="Réinitialiser"
              onClick={resetFilters}
              variant="outline"
              className="!rounded-xl"
            />
          </div>

          {/* Compteur */}
          <div className="text-sm text-slate-600 dark:text-slate-300">
            {filteredSorted.length} annonce{filteredSorted.length > 1 ? "s" : ""} affichée
            {filteredSorted.length > 1 ? "s" : ""}.
          </div>
        </div>
      </div>

      {/* Grid */}
      <div className="max-w-[1280px] mx-auto px-4 mt-6 mb-12">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className="rounded-2xl overflow-hidden bg-white/90 dark:bg-slate-800/80 ring-1 ring-slate-200/70 dark:ring-slate-700/70"
              >
                <div className="h-48 bg-slate-200 dark:bg-slate-700 animate-pulse" />
                <div className="p-4">
                  <div className="h-5 w-2/3 bg-slate-200 dark:bg-slate-700 animate-pulse rounded mb-2" />
                  <div className="h-4 w-full bg-slate-200 dark:bg-slate-700 animate-pulse rounded mb-2" />
                  <div className="h-4 w-3/4 bg-slate-200 dark:bg-slate-700 animate-pulse rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : filteredSorted.length === 0 ? (
          <div className="rounded-2xl p-6 text-center ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/90 dark:bg-slate-800/80">
            Aucune annonce ne correspond à vos critères.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {filteredSorted.map((annonce) => (
              <AnnonceCard
                key={annonce.id}
                annonce={annonce}
                refreshList={reload}
                currentUserId={currentUserId}
                canModerate={canModerate}
              />
            ))}
          </div>
        )}
      </div>

      {/* Modale création */}
      {canCreate && (
        <CreateAnnonceModal
          isOpen={isCreateModalOpen}
          onClose={closeCreate}
          userId={currentUserId}
          afterCreate={closeCreate}
        />
      )}
    </div>
  );
};

export default AnnoncesPage;
